import 'ag-grid-community/styles/ag-grid.css';
import 'ag-grid-community/styles/ag-theme-alpine.css';
import 'ag-grid-enterprise';
import { AgGridReact } from 'ag-grid-react';
import ColumnSidepanel from 'common/filters/column-sidepanel';
import SidePanel from 'common/side-panel/side-panel';
import Spinner from 'common/spinner/components/spinner';
import debounce from 'lodash.debounce';
import isEqual from 'lodash.isequal';
import PropTypes from 'prop-types';
import React, { useCallback, useState } from 'react';
import { isToggleEnabled } from 'react-awesome-feature-toggles';
import { FormattedMessage, useIntl } from 'react-intl';
import styled from 'styled-components';
import { Button, Flex, Icon, SapId } from 'ui';
import { decodeUrlParams } from 'utils/helpers';
import { withRouter } from 'utils/withRouter';
import { sortCOPSModelToParam, sortParamToAGModel } from '../../../utils/helpers';

const Results = styled(Flex)`
  padding: 20px;
  flex-wrap: wrap;
  width: 100%;
  height: 100%;
`;

const StyledButton = styled(Button)`
  margin-right: 15px;
  background-color: ${p => p.theme.colors.theme} !important;
  width: 35px !important;
  height: 35px !important;
  cursor: pointer;
  border-radius: 4px;
  display: flex;
  align-items: center;
  justify-content: center;
  i, img {
    cursor: pointer;
    color: ${p => p.theme.colors.brokenWhite.backgroundColor} !important;
    width: 20px !important;
    height: 20px !important;
    line-height: 20px;
  }
`;

const Link = styled.p`
  color: ${p => p.theme.colors.theme};
  text-overflow: ellipsis;
  overflow: hidden;
  cursor: pointer;
  &:hover {
    text-decoration: underline;
    text-decoration-color: ${p => p.theme.colors.theme};
  }
`;

const Wrapper = styled.div`
  height: calc(100vh - 150px);
  width: 100%;
`;

const Data = styled.div`
  height: 100%;
`;

const SidepanelToggle = styled(Icon)`
  margin-left: auto;
  font-size: 20px;
  margin-top: 6px;
  cursor: ${p => p.disabled ? 'not-allowed' : 'pointer'};
  color: ${p => p.disabled ? p.theme.colors.themeSecondary : p.theme.colors.theme};
  opacity: ${p => p.disabled ? 0.3 : 1};
  transition: all 0.5s linear;
  transform-origin: center;
  width: 20px !important;
  height: 20px !important;
  transform: ${p => p.isOpen ? 'rotate(60deg)' : 'rotate(-60deg)'};
`;

const Header = styled.div`
  height: 7%;
  margin-bottom: 15px;
  display: flex;
  flex-flow: row nowrap;
  align-items: center;
  width: 100%;
  span {
    margin-left: 5px;
    text-decoration: underline;
    cursor: pointer;
    color: ${p => p.theme.colors.theme};
  }
`;

const Grid = styled.div`
  --ag-range-selection-highlight-color: rgb(33, 150, 243, 0.3);
  .ag-sort-order:not(.ag-hidden) {
    font-weight: 300;
    font-size: 12px;
    &:before {
      content: "(";
    }
    &:after {
      content: ")";
    }
  }
`;

const Back = styled.div`
  color: ${p => p.theme.colors.theme};
  cursor: pointer;
  width: 100%;
  margin-bottom: 35px;
  height: 20px;
`;

const MainText = styled.span`
  text-decoration: underline;
  font-weight: bold;
`;

const Warning = styled.div`
  margin-left: 20px;
  color: ${p => p.theme.colors.alerts[2]} !important;
  font-weight: bold;
  i {
    margin-right: 5px;
    color: ${p => p.theme.colors.alerts[2]} !important;
  }
`;

const SubText = styled.span`
  color: #b9b9b9;
  font-weight: 400;
  text-decoration: none;
  margin-left: 5px;
`;

const StyledIcon = styled(Icon)`
  margin-right: 15px;
  cursor: pointer;
`;

const getLinkRenderer = (value, uri, history) => uri ?
  <Link onClick={() => history.push(`/${uri}`)}>
    {value}
  </Link> :
  value;

const SearchRelations = ({
  history,
  match,
  isLoading,
  results = {},
}) => {
  const intl = useIntl();
  const [cols, setCols] = useState([]);
  const [isSidepanelOpen, setIsSidepanelOpen] = useState(false);
  const urlParams = decodeUrlParams(match.params);
  const { header = {}, data = [], columns = [], showMoreItemsWarning = false } = results;

  const goBack = () => {
    const searchFilters = localStorage.getItem('searchFilters');
    let queryString = '';
    if (searchFilters) {
      queryString = searchFilters;
    }
    const url = history.location.pathname
      .replace(/\/relations\/.*?(?=\/|$)/, '');
    history.push(url, {
      searchFilters: queryString ? encodeURIComponent(queryString) : '',
    });
  };

  const getCols = (s, localCols) => s?.map((c) => {
    const localData = localCols.find(l => l.id === c.id);
    c.width = localData?.width ?? c.width;
    c.hide = localData ? localData.hide : c.hide;
    c.index = localData?.index ?? c.index;
    if (c.id?.toLowerCase() === 'customername') {
      return {
        ...c,
        cellRenderer: ({ data: d, value }) => getLinkRenderer(value, d.customerUri, history),
      };
    } else if (c.id?.toLowerCase() === 'equipmentname') {
      return {
        ...c,
        cellRenderer: ({ data: d, value }) => getLinkRenderer(value, d.equipmentUri, history),
      };
    } else if (c.id?.toLowerCase() === 'installedatname') {
      return {
        ...c,
        cellRenderer: ({ data: d, value }) => getLinkRenderer(value, d.installedAtUri, history),
      };
    } else if (c.id?.toLowerCase() === 'functionallocationname') {
      return {
        ...c,
        cellRenderer: ({ data: d, value }) =>
          getLinkRenderer(
            value,
            isToggleEnabled('functionalLocationInfo') && d.functionalLocationUri,
            history,
          ),
      };
    } else if (c.type?.toLowerCase() === 'sapid') {
      return {
        ...c,
        cellRenderer: ({ value }) =>
          <SapId originalId={value}>
            {id => <span>{id}</span>}
          </SapId>,
      };
    }
    return c;
  })?.sort((a, b) => a.index - b.index);


  const handleGridReady = () => {
    const defaultCols = columns.map(c => ({
      id: c.id,
      width: c.width,
      index: c.index,
      hide: c.hide,
    }));
    let localCols = localStorage.getItem('relationstable') ?
      JSON.parse(localStorage.getItem('relationstable')) :
      null;
    if (
      !localCols ||
      !isEqual(
        columns.map(c => c.id).sort(),
        localCols?.map(c => c.id).sort(),
      )
    ) {
      localCols = defaultCols;
      localStorage.setItem('relationstable', JSON.stringify(defaultCols));
    }
    setCols(getCols(columns, localCols));
  };

  const handleColumnChange = ({ column, toIndex }, shouldUpdate) => {
    if (column) {
      const localString = localStorage.getItem('relationstable');
      const local = JSON.parse(localString);
      const movedColumn = (toIndex || toIndex === 0) &&
        local.find(l => l.id === column.colDef.id);
      const newLocal = local.map((l) => {
        if (l.id === column.colDef.id) {
          return {
            ...l,
            width: column.actualWidth ?? l.width,
            index: toIndex ?? l.index,
            hide: typeof column.hide === 'boolean' ? column.hide : l.hide,
          };
        }
        // Pos change
        if (movedColumn) {
          if (toIndex > movedColumn.index && l.index > movedColumn.index && l.index <= toIndex) {
            return {
              ...l,
              index: l.index - 1,
            };
          } else if (
            toIndex < movedColumn.index &&
            l.index < movedColumn.index &&
            l.index >= toIndex
          ) {
            return {
              ...l,
              index: l.index + 1,
            };
          }
        }
        return l;
      });
      localStorage.setItem(
        'relationstable',
        JSON.stringify(newLocal.sort((a, b) => a.index - b.index)),
      );
      if (shouldUpdate) {
        setCols(s => getCols(s, newLocal.sort((a, b) => a.index - b.index)));
      }
    }
  };

  const handleUpdate = useCallback(
    debounce((e, update) => e.finished && handleColumnChange(e, update), 300),
    [],
  );

  const handleSort = (e) => {
    const sortModel = sortParamToAGModel(e);
    setCols(s => s.map((c) => {
      const model = sortModel.find(v => v.colId === c.field);
      if (model) {
        return {
          ...c,
          ...model,
        };
      }
      return {
        ...c,
        sort: null,
        sortIndex: null,
      };
    }));
  };

  return (
    <>
      <Results ffrn>
        <Back onClick={goBack}>
          <MainText>
            <StyledIcon type='arrow-left' />
            <FormattedMessage id='search.BACK_TO_OVERVIEW' />
          </MainText>
          <SubText>
            <FormattedMessage id='search.FOR_TERM' values={{ term: urlParams.searchValue }} />
          </SubText>
        </Back>
        <Wrapper>
          {isLoading ?
            <Spinner /> :
            <Data>
              <Header>
                <StyledButton onClick={goBack}>
                  <Icon
                    type='project-diagram-cross'
                    customIcon={true}
                  />
                </StyledButton>
                <FormattedMessage id='search.LINKED_ITEMS_FOR' />{': '}
                <span onClick={() => history.push(`/${header.uri}`, { context: header.id })} data-test-id='header-name'>
                  {header.name}
                </span>
                {showMoreItemsWarning ?
                  <Warning>
                    <Icon type='triangle-exclamation' size='lg' />
                    <FormattedMessage id='search.RELATIONS_LIMIT_WARNING' />
                  </Warning> :
                  null}
                <SidepanelToggle
                  onClick={() => setIsSidepanelOpen(true)}
                  type='gear'
                  isOpen={isSidepanelOpen}
                  disabled={isLoading}
                />
              </Header>
              <Grid
                className='ag-theme-alpine'
                style={{ height: 'calc(93% - 15px)' }}
              >
                <AgGridReact
                  rowData={data}
                  columnDefs={cols}
                  onGridReady={handleGridReady}
                  alwaysMultiSort={true}
                  suppressCsvExport={true}
                  suppressExcelExport={true}
                  onColumnResized={e => handleUpdate(e)}
                  onColumnMoved={e => handleUpdate(e, true)}
                  onSortChanged={e => setCols(e.api.getColumnDefs())}
                  // use onColumnVisible even to allow inline show/hide column
                  // If select all must be supported, adjust handleColumnChange to look a columnS
                  // remove line below to allow inline show/hide and hide on drag outside table
                  suppressDragLeaveHidesColumns={true}
                />
              </Grid>
            </Data>
          }
        </Wrapper>
      </Results>
      {isSidepanelOpen ?
        <SidePanel
          width='40%'
          title={intl.formatMessage({ id: 'worklist.filters.EDIT_COLUMNS' })}
          onPanelClose={() => setIsSidepanelOpen(false)}
        >
          <ColumnSidepanel
            localData={{
              columns: cols.map(c => ({
                ...c,
                colId: c.field,
                isHidden: c.hide,
              })),
              onChange: e => handleColumnChange(e, true),
              defaultSorting: sortCOPSModelToParam(cols.map(c => c.defaultSorting).filter(s => s)),
              onSort: handleSort,
              activeSort: sortCOPSModelToParam(cols.map((c) => {
                if (c.sort) {
                  return {
                    sortDirection: c.sort,
                    sortIndex: c.sortIndex,
                    sortColumn: c.field,
                  };
                }
                return null;
              }).filter(s => s)),
            }}
          />
        </SidePanel> :
        null
      }
    </>
  );
};

SearchRelations.propTypes = {
  results: PropTypes.object.isRequired,
  history: PropTypes.object,
  match: PropTypes.object,
  isLoading: PropTypes.bool,
};

export default withRouter(SearchRelations);
